/**
 * Industry UI Configuration
 * Centralized color schemes and styling for all industries
 * Used across CRM, Demo, and Live Account systems for consistency
 * 
 * COLOR PHILOSOPHY: Each industry has a DISTINCT, easily identifiable color
 * that is visually unique from other industries for quick recognition
 */

import {
  ChefHat, ShoppingBag, Heart, Briefcase, Dumbbell, Home,
  Building2, Zap, GraduationCap, Sparkles, Factory, MapPin,
  Target, Code, Wallet, Stethoscope, Car, Scissors, Plane, Flame,
  Mountain, Shield, ClipboardList
} from 'lucide-react';

// Industry-specific UI color schemes - consistent across entire system
export const INDUSTRY_UI_CONFIG = {
  hospitality: {
    id: 'hospitality',
    name: 'Hospitality & Food Services',
    shortName: 'Hospitality',
    icon: ChefHat,
    // Deep Purple - Luxurious, upscale dining feel
    gradient: 'from-purple-600 via-violet-600 to-purple-700',
    gradientHover: 'from-purple-700 via-violet-700 to-purple-800',
    gradientLight: 'from-purple-100 via-violet-100 to-purple-100',
    gradientSubtle: 'from-purple-50 via-violet-50 to-purple-50',
    primary: '#9333EA', // purple-600
    primaryLight: '#A855F7', // purple-500
    primaryDark: '#7E22CE', // purple-700
    bgLight: 'bg-purple-50',
    bgMedium: 'bg-purple-100',
    bgAccent: 'bg-purple-600',
    textColor: 'text-purple-700',
    textColorLight: 'text-purple-500',
    textColorDark: 'text-purple-900',
    borderColor: 'border-purple-400',
    borderColorLight: 'border-purple-200',
    borderColorDark: 'border-purple-500',
    ringColor: 'ring-purple-400/50',
    shadowColor: 'shadow-purple-500/25',
    shadowColorStrong: 'shadow-purple-600/40',
    iconBg: 'bg-gradient-to-br from-purple-500 via-violet-600 to-purple-700',
    iconInnerBg: 'bg-gradient-to-br from-purple-600 to-violet-700',
    iconRing: 'ring-purple-300/60',
    iconShadow: '0 8px 32px rgba(147, 51, 234, 0.4)',
    hex: '#9333EA',
    hexLight: '#FAF5FF',
    hexDark: '#6B21A8'
  },
  retail: {
    id: 'retail',
    name: 'Retail & E-commerce',
    shortName: 'Retail',
    icon: ShoppingBag,
    // Vibrant Blue - Trust, commerce
    gradient: 'from-blue-500 via-blue-600 to-blue-700',
    gradientHover: 'from-blue-600 via-blue-700 to-blue-800',
    gradientLight: 'from-blue-100 via-blue-50 to-blue-100',
    gradientSubtle: 'from-blue-50 via-sky-50 to-blue-50',
    primary: '#3B82F6', // blue-500
    primaryLight: '#60A5FA', // blue-400
    primaryDark: '#2563EB', // blue-600
    bgLight: 'bg-blue-50',
    bgMedium: 'bg-blue-100',
    bgAccent: 'bg-blue-500',
    textColor: 'text-blue-700',
    textColorLight: 'text-blue-500',
    textColorDark: 'text-blue-900',
    borderColor: 'border-blue-400',
    borderColorLight: 'border-blue-200',
    borderColorDark: 'border-blue-500',
    ringColor: 'ring-blue-400/50',
    shadowColor: 'shadow-blue-500/25',
    shadowColorStrong: 'shadow-blue-600/40',
    iconBg: 'bg-gradient-to-br from-blue-400 via-blue-500 to-blue-600',
    iconInnerBg: 'bg-gradient-to-br from-blue-500 to-blue-700',
    iconRing: 'ring-blue-300/60',
    iconShadow: '0 8px 32px rgba(59, 130, 246, 0.4)',
    hex: '#3B82F6',
    hexLight: '#EFF6FF',
    hexDark: '#1D4ED8'
  },
  healthcare: {
    id: 'healthcare',
    name: 'Healthcare & Medical',
    shortName: 'Healthcare',
    icon: Stethoscope,
    // Hot Pink - Care, compassion, medical
    gradient: 'from-pink-500 via-pink-600 to-rose-600',
    gradientHover: 'from-pink-600 via-pink-700 to-rose-700',
    gradientLight: 'from-pink-100 via-pink-50 to-rose-100',
    gradientSubtle: 'from-pink-50 via-rose-50 to-pink-50',
    primary: '#EC4899', // pink-500
    primaryLight: '#F472B6', // pink-400
    primaryDark: '#DB2777', // pink-600
    bgLight: 'bg-pink-50',
    bgMedium: 'bg-pink-100',
    bgAccent: 'bg-pink-500',
    textColor: 'text-pink-700',
    textColorLight: 'text-pink-500',
    textColorDark: 'text-pink-900',
    borderColor: 'border-pink-400',
    borderColorLight: 'border-pink-200',
    borderColorDark: 'border-pink-500',
    ringColor: 'ring-pink-400/50',
    shadowColor: 'shadow-pink-500/25',
    shadowColorStrong: 'shadow-pink-600/40',
    iconBg: 'bg-gradient-to-br from-pink-400 via-pink-500 to-rose-600',
    iconInnerBg: 'bg-gradient-to-br from-pink-500 to-rose-600',
    iconRing: 'ring-pink-300/60',
    iconShadow: '0 8px 32px rgba(236, 72, 153, 0.4)',
    hex: '#EC4899',
    hexLight: '#FDF2F8',
    hexDark: '#BE185D'
  },
  professional: {
    id: 'professional',
    name: 'Professional Services',
    shortName: 'Professional',
    icon: Briefcase,
    // Navy Slate - Corporate, professional, trustworthy
    gradient: 'from-slate-600 via-slate-700 to-slate-800',
    gradientHover: 'from-slate-700 via-slate-800 to-slate-900',
    gradientLight: 'from-slate-100 via-slate-50 to-slate-100',
    gradientSubtle: 'from-slate-50 via-gray-50 to-slate-50',
    primary: '#475569', // slate-600
    primaryLight: '#64748B', // slate-500
    primaryDark: '#334155', // slate-700
    bgLight: 'bg-slate-50',
    bgMedium: 'bg-slate-100',
    bgAccent: 'bg-slate-600',
    textColor: 'text-slate-700',
    textColorLight: 'text-slate-500',
    textColorDark: 'text-slate-900',
    borderColor: 'border-slate-400',
    borderColorLight: 'border-slate-200',
    borderColorDark: 'border-slate-500',
    ringColor: 'ring-slate-400/50',
    shadowColor: 'shadow-slate-500/25',
    shadowColorStrong: 'shadow-slate-600/40',
    iconBg: 'bg-gradient-to-br from-slate-500 via-slate-600 to-slate-700',
    iconInnerBg: 'bg-gradient-to-br from-slate-600 to-slate-800',
    iconRing: 'ring-slate-300/60',
    iconShadow: '0 8px 32px rgba(71, 85, 105, 0.4)',
    hex: '#475569',
    hexLight: '#F8FAFC',
    hexDark: '#1E293B'
  },
  fitness: {
    id: 'fitness',
    name: 'Fitness & Wellness',
    shortName: 'Fitness',
    icon: Dumbbell,
    // Lime Green - Energy, vitality, health
    gradient: 'from-lime-500 via-green-500 to-lime-600',
    gradientHover: 'from-lime-600 via-green-600 to-lime-700',
    gradientLight: 'from-lime-100 via-green-100 to-lime-100',
    gradientSubtle: 'from-lime-50 via-green-50 to-lime-50',
    primary: '#84CC16', // lime-500
    primaryLight: '#A3E635', // lime-400
    primaryDark: '#65A30D', // lime-600
    bgLight: 'bg-lime-50',
    bgMedium: 'bg-lime-100',
    bgAccent: 'bg-lime-500',
    textColor: 'text-lime-700',
    textColorLight: 'text-lime-600',
    textColorDark: 'text-lime-900',
    borderColor: 'border-lime-400',
    borderColorLight: 'border-lime-200',
    borderColorDark: 'border-lime-500',
    ringColor: 'ring-lime-400/50',
    shadowColor: 'shadow-lime-500/25',
    shadowColorStrong: 'shadow-lime-600/40',
    iconBg: 'bg-gradient-to-br from-lime-400 via-green-500 to-lime-600',
    iconInnerBg: 'bg-gradient-to-br from-lime-500 to-green-600',
    iconRing: 'ring-lime-300/60',
    iconShadow: '0 8px 32px rgba(132, 204, 22, 0.4)',
    hex: '#84CC16',
    hexLight: '#F7FEE7',
    hexDark: '#4D7C0F'
  },
  property: {
    id: 'property',
    name: 'Property & Real Estate',
    shortName: 'Property',
    icon: Home,
    // Warm Orange - Homes, warmth, welcome
    gradient: 'from-orange-500 via-orange-600 to-amber-600',
    gradientHover: 'from-orange-600 via-orange-700 to-amber-700',
    gradientLight: 'from-orange-100 via-orange-50 to-amber-100',
    gradientSubtle: 'from-orange-50 via-amber-50 to-orange-50',
    primary: '#F97316', // orange-500
    primaryLight: '#FB923C', // orange-400
    primaryDark: '#EA580C', // orange-600
    bgLight: 'bg-orange-50',
    bgMedium: 'bg-orange-100',
    bgAccent: 'bg-orange-500',
    textColor: 'text-orange-700',
    textColorLight: 'text-orange-500',
    textColorDark: 'text-orange-900',
    borderColor: 'border-orange-400',
    borderColorLight: 'border-orange-200',
    borderColorDark: 'border-orange-500',
    ringColor: 'ring-orange-400/50',
    shadowColor: 'shadow-orange-500/25',
    shadowColorStrong: 'shadow-orange-600/40',
    iconBg: 'bg-gradient-to-br from-orange-400 via-orange-500 to-amber-600',
    iconInnerBg: 'bg-gradient-to-br from-orange-500 to-amber-600',
    iconRing: 'ring-orange-300/60',
    iconShadow: '0 8px 32px rgba(249, 115, 22, 0.4)',
    hex: '#F97316',
    hexLight: '#FFF7ED',
    hexDark: '#C2410C'
  },
  construction: {
    id: 'construction',
    name: 'Construction & Building',
    shortName: 'Construction',
    icon: Building2,
    // Golden Yellow - Safety, construction, building
    gradient: 'from-yellow-400 via-yellow-500 to-amber-500',
    gradientHover: 'from-yellow-500 via-yellow-600 to-amber-600',
    gradientLight: 'from-yellow-100 via-yellow-50 to-amber-100',
    gradientSubtle: 'from-yellow-50 via-amber-50 to-yellow-50',
    primary: '#EAB308', // yellow-500
    primaryLight: '#FACC15', // yellow-400
    primaryDark: '#CA8A04', // yellow-600
    bgLight: 'bg-yellow-50',
    bgMedium: 'bg-yellow-100',
    bgAccent: 'bg-yellow-500',
    textColor: 'text-yellow-700',
    textColorLight: 'text-yellow-600',
    textColorDark: 'text-yellow-900',
    borderColor: 'border-yellow-400',
    borderColorLight: 'border-yellow-200',
    borderColorDark: 'border-yellow-500',
    ringColor: 'ring-yellow-400/50',
    shadowColor: 'shadow-yellow-500/25',
    shadowColorStrong: 'shadow-yellow-600/40',
    iconBg: 'bg-gradient-to-br from-yellow-400 via-yellow-500 to-amber-500',
    iconInnerBg: 'bg-gradient-to-br from-yellow-500 to-amber-600',
    iconRing: 'ring-yellow-300/60',
    iconShadow: '0 8px 32px rgba(234, 179, 8, 0.4)',
    hex: '#EAB308',
    hexLight: '#FEFCE8',
    hexDark: '#A16207'
  },
  automotive: {
    id: 'automotive',
    name: 'Automotive & Vehicles',
    shortName: 'Automotive',
    icon: Car,
    // Deep Red - Speed, power, automotive
    gradient: 'from-red-500 via-red-600 to-red-700',
    gradientHover: 'from-red-600 via-red-700 to-red-800',
    gradientLight: 'from-red-100 via-red-50 to-red-100',
    gradientSubtle: 'from-red-50 via-rose-50 to-red-50',
    primary: '#EF4444', // red-500
    primaryLight: '#F87171', // red-400
    primaryDark: '#DC2626', // red-600
    bgLight: 'bg-red-50',
    bgMedium: 'bg-red-100',
    bgAccent: 'bg-red-500',
    textColor: 'text-red-700',
    textColorLight: 'text-red-500',
    textColorDark: 'text-red-900',
    borderColor: 'border-red-400',
    borderColorLight: 'border-red-200',
    borderColorDark: 'border-red-500',
    ringColor: 'ring-red-400/50',
    shadowColor: 'shadow-red-500/25',
    shadowColorStrong: 'shadow-red-600/40',
    iconBg: 'bg-gradient-to-br from-red-400 via-red-500 to-red-700',
    iconInnerBg: 'bg-gradient-to-br from-red-500 to-red-700',
    iconRing: 'ring-red-300/60',
    iconShadow: '0 8px 32px rgba(239, 68, 68, 0.4)',
    hex: '#EF4444',
    hexLight: '#FEF2F2',
    hexDark: '#B91C1C'
  },
  education: {
    id: 'education',
    name: 'Education & Training',
    shortName: 'Education',
    icon: GraduationCap,
    // Teal - Knowledge, learning, growth
    gradient: 'from-[#0066CC] via-[#0055B5] to-[#0066CC]',
    gradientHover: 'from-[#0055B5] via-[#004A99] to-cyan-700',
    gradientLight: 'from-blue-100 via-blue-50 to-cyan-100',
    gradientSubtle: 'from-blue-50 via-cyan-50 to-blue-50',
    primary: '#14B8A6', // [#0066CC]
    primaryLight: '#2DD4BF', // [#007AFF]
    primaryDark: '#0D9488', // [#0055B5]
    bgLight: 'bg-blue-50',
    bgMedium: 'bg-blue-100',
    bgAccent: 'bg-[#0066CC]',
    textColor: 'text-[#004A99]',
    textColorLight: 'text-[#0066CC]',
    textColorDark: 'text-teal-900',
    borderColor: 'border-[#007AFF]',
    borderColorLight: 'border-teal-200',
    borderColorDark: 'border-[#0066CC]',
    ringColor: 'ring-[#007AFF]/50',
    shadowColor: 'shadow-[#0066CC]/25',
    shadowColorStrong: 'shadow-[#0055B5]/40',
    iconBg: 'bg-gradient-to-br from-[#007AFF] via-[#0066CC] to-[#0066CC]',
    iconInnerBg: 'bg-gradient-to-br from-[#0066CC] to-[#0066CC]',
    iconRing: 'ring-[#4DA6FF]/60',
    iconShadow: '0 8px 32px rgba(20, 184, 166, 0.4)',
    hex: '#14B8A6',
    hexLight: '#F0FDFA',
    hexDark: '#0F766E'
  },
  beauty: {
    id: 'beauty',
    name: 'Beauty & Salon',
    shortName: 'Beauty',
    icon: Scissors,
    // Fuchsia/Magenta - Glamour, beauty, luxury
    gradient: 'from-fuchsia-500 via-fuchsia-600 to-pink-600',
    gradientHover: 'from-fuchsia-600 via-fuchsia-700 to-pink-700',
    gradientLight: 'from-fuchsia-100 via-fuchsia-50 to-pink-100',
    gradientSubtle: 'from-fuchsia-50 via-pink-50 to-fuchsia-50',
    primary: '#D946EF', // fuchsia-500
    primaryLight: '#E879F9', // fuchsia-400
    primaryDark: '#C026D3', // fuchsia-600
    bgLight: 'bg-fuchsia-50',
    bgMedium: 'bg-fuchsia-100',
    bgAccent: 'bg-fuchsia-500',
    textColor: 'text-fuchsia-700',
    textColorLight: 'text-fuchsia-500',
    textColorDark: 'text-fuchsia-900',
    borderColor: 'border-fuchsia-400',
    borderColorLight: 'border-fuchsia-200',
    borderColorDark: 'border-fuchsia-500',
    ringColor: 'ring-fuchsia-400/50',
    shadowColor: 'shadow-fuchsia-500/25',
    shadowColorStrong: 'shadow-fuchsia-600/40',
    iconBg: 'bg-gradient-to-br from-fuchsia-400 via-fuchsia-500 to-pink-600',
    iconInnerBg: 'bg-gradient-to-br from-fuchsia-500 to-pink-600',
    iconRing: 'ring-fuchsia-300/60',
    iconShadow: '0 8px 32px rgba(217, 70, 239, 0.4)',
    hex: '#D946EF',
    hexLight: '#FDF4FF',
    hexDark: '#A21CAF'
  },
  manufacturing: {
    id: 'manufacturing',
    name: 'Manufacturing & Industrial',
    shortName: 'Manufacturing',
    icon: Factory,
    // Steel Gray/Zinc - Industrial, machinery
    gradient: 'from-zinc-500 via-zinc-600 to-stone-600',
    gradientHover: 'from-zinc-600 via-zinc-700 to-stone-700',
    gradientLight: 'from-zinc-100 via-zinc-50 to-stone-100',
    gradientSubtle: 'from-zinc-50 via-stone-50 to-zinc-50',
    primary: '#71717A', // zinc-500
    primaryLight: '#A1A1AA', // zinc-400
    primaryDark: '#52525B', // zinc-600
    bgLight: 'bg-zinc-50',
    bgMedium: 'bg-zinc-100',
    bgAccent: 'bg-zinc-500',
    textColor: 'text-zinc-700',
    textColorLight: 'text-zinc-500',
    textColorDark: 'text-zinc-900',
    borderColor: 'border-zinc-400',
    borderColorLight: 'border-zinc-200',
    borderColorDark: 'border-zinc-500',
    ringColor: 'ring-zinc-400/50',
    shadowColor: 'shadow-zinc-500/25',
    shadowColorStrong: 'shadow-zinc-600/40',
    iconBg: 'bg-gradient-to-br from-zinc-400 via-zinc-500 to-stone-600',
    iconInnerBg: 'bg-gradient-to-br from-zinc-500 to-stone-600',
    iconRing: 'ring-zinc-300/60',
    iconShadow: '0 8px 32px rgba(113, 113, 122, 0.4)',
    hex: '#71717A',
    hexLight: '#FAFAFA',
    hexDark: '#3F3F46'
  },
  tourism: {
    id: 'tourism',
    name: 'Tourism & Travel',
    shortName: 'Tourism',
    icon: Plane,
    // Sky Blue - Sky, travel, freedom
    gradient: 'from-sky-400 via-sky-500 to-blue-500',
    gradientHover: 'from-sky-500 via-sky-600 to-blue-600',
    gradientLight: 'from-sky-100 via-sky-50 to-blue-100',
    gradientSubtle: 'from-sky-50 via-blue-50 to-sky-50',
    primary: '#0EA5E9', // sky-500
    primaryLight: '#38BDF8', // sky-400
    primaryDark: '#0284C7', // sky-600
    bgLight: 'bg-sky-50',
    bgMedium: 'bg-sky-100',
    bgAccent: 'bg-sky-500',
    textColor: 'text-sky-700',
    textColorLight: 'text-sky-500',
    textColorDark: 'text-sky-900',
    borderColor: 'border-sky-400',
    borderColorLight: 'border-sky-200',
    borderColorDark: 'border-sky-500',
    ringColor: 'ring-sky-400/50',
    shadowColor: 'shadow-sky-500/25',
    shadowColorStrong: 'shadow-sky-600/40',
    iconBg: 'bg-gradient-to-br from-sky-400 via-sky-500 to-blue-500',
    iconInnerBg: 'bg-gradient-to-br from-sky-500 to-blue-600',
    iconRing: 'ring-sky-300/60',
    iconShadow: '0 8px 32px rgba(14, 165, 233, 0.4)',
    hex: '#0EA5E9',
    hexLight: '#F0F9FF',
    hexDark: '#0369A1'
  },
  crm: {
    id: 'crm',
    name: 'CRM & Sales',
    shortName: 'CRM',
    icon: Target,
    // Violet - Digital, modern, tech-forward
    gradient: 'from-violet-500 via-violet-600 to-indigo-600',
    gradientHover: 'from-violet-600 via-violet-700 to-indigo-700',
    gradientLight: 'from-violet-100 via-violet-50 to-indigo-100',
    gradientSubtle: 'from-violet-50 via-indigo-50 to-violet-50',
    primary: '#8B5CF6', // violet-500
    primaryLight: '#A78BFA', // violet-400
    primaryDark: '#7C3AED', // violet-600
    bgLight: 'bg-violet-50',
    bgMedium: 'bg-violet-100',
    bgAccent: 'bg-violet-500',
    textColor: 'text-violet-700',
    textColorLight: 'text-violet-500',
    textColorDark: 'text-violet-900',
    borderColor: 'border-violet-400',
    borderColorLight: 'border-violet-200',
    borderColorDark: 'border-violet-500',
    ringColor: 'ring-violet-400/50',
    shadowColor: 'shadow-violet-500/25',
    shadowColorStrong: 'shadow-violet-600/40',
    iconBg: 'bg-gradient-to-br from-violet-400 via-violet-500 to-indigo-600',
    iconInnerBg: 'bg-gradient-to-br from-violet-500 to-indigo-600',
    iconRing: 'ring-violet-300/60',
    iconShadow: '0 8px 32px rgba(139, 92, 246, 0.4)',
    hex: '#8B5CF6',
    hexLight: '#F5F3FF',
    hexDark: '#6D28D9'
  },
  technology: {
    id: 'technology',
    name: 'Technology & Software',
    shortName: 'Technology',
    icon: Code,
    // Electric Cyan - Digital, innovation, tech
    gradient: 'from-[#4DA6FF] via-[#007AFF] to-[#0066CC]',
    gradientHover: 'from-[#007AFF] via-[#0066CC] to-[#0055B5]',
    gradientLight: 'from-cyan-100 via-cyan-50 to-blue-100',
    gradientSubtle: 'from-cyan-50 via-blue-50 to-cyan-50',
    primary: '#06B6D4', // [#007AFF]
    primaryLight: '#22D3EE', // [#4DA6FF]
    primaryDark: '#0891B2', // [#0066CC]
    bgLight: 'bg-cyan-50',
    bgMedium: 'bg-cyan-100',
    bgAccent: 'bg-[#007AFF]',
    textColor: 'text-cyan-700',
    textColorLight: 'text-[#007AFF]',
    textColorDark: 'text-cyan-900',
    borderColor: 'border-[#4DA6FF]',
    borderColorLight: 'border-cyan-200',
    borderColorDark: 'border-[#007AFF]',
    ringColor: 'ring-[#4DA6FF]/50',
    shadowColor: 'shadow-[#007AFF]/25',
    shadowColorStrong: 'shadow-[#0066CC]/40',
    iconBg: 'bg-gradient-to-br from-[#4DA6FF] via-[#007AFF] to-[#0066CC]',
    iconInnerBg: 'bg-gradient-to-br from-[#007AFF] to-[#0055B5]',
    iconRing: 'ring-cyan-300/60',
    iconShadow: '0 8px 32px rgba(6, 182, 212, 0.4)',
    hex: '#06B6D4',
    hexLight: '#ECFEFF',
    hexDark: '#0E7490'
  },
  finance: {
    id: 'finance',
    name: 'Finance & Banking',
    shortName: 'Finance',
    icon: Wallet,
    // Emerald Green - Money, prosperity, growth
    gradient: 'from-emerald-500 via-emerald-600 to-green-600',
    gradientHover: 'from-emerald-600 via-emerald-700 to-green-700',
    gradientLight: 'from-emerald-100 via-emerald-50 to-green-100',
    gradientSubtle: 'from-emerald-50 via-green-50 to-emerald-50',
    primary: '#10B981', // emerald-500
    primaryLight: '#34D399', // emerald-400
    primaryDark: '#059669', // emerald-600
    bgLight: 'bg-emerald-50',
    bgMedium: 'bg-emerald-100',
    bgAccent: 'bg-emerald-500',
    textColor: 'text-emerald-700',
    textColorLight: 'text-emerald-500',
    textColorDark: 'text-emerald-900',
    borderColor: 'border-emerald-400',
    borderColorLight: 'border-emerald-200',
    borderColorDark: 'border-emerald-500',
    ringColor: 'ring-emerald-400/50',
    shadowColor: 'shadow-emerald-500/25',
    shadowColorStrong: 'shadow-emerald-600/40',
    iconBg: 'bg-gradient-to-br from-emerald-400 via-emerald-500 to-green-600',
    iconInnerBg: 'bg-gradient-to-br from-emerald-500 to-green-600',
    iconRing: 'ring-emerald-300/60',
    iconShadow: '0 8px 32px rgba(16, 185, 129, 0.4)',
    hex: '#10B981',
    hexLight: '#ECFDF5',
    hexDark: '#047857'
  },
  qsr: {
    id: 'qsr',
    name: 'Quick Service Restaurant',
    shortName: 'QSR',
    icon: Flame,
    // Amber/Warm Orange - Fast food energy, warmth, grilling
    gradient: 'from-amber-600 via-orange-500 to-amber-600',
    gradientHover: 'from-amber-700 via-orange-600 to-amber-700',
    gradientLight: 'from-amber-100 via-orange-100 to-amber-100',
    gradientSubtle: 'from-amber-50 via-orange-50 to-amber-50',
    primary: '#D97706',
    primaryLight: '#F59E0B',
    primaryDark: '#B45309',
    bgLight: 'bg-amber-50',
    bgMedium: 'bg-amber-100',
    bgAccent: 'bg-amber-600',
    textColor: 'text-amber-700',
    textColorLight: 'text-amber-500',
    textColorDark: 'text-amber-900',
    borderColor: 'border-amber-400',
    borderColorLight: 'border-amber-200',
    borderColorDark: 'border-amber-500',
    ringColor: 'ring-amber-400/50',
    shadowColor: 'shadow-amber-500/25',
    shadowColorStrong: 'shadow-amber-600/40',
    iconBg: 'bg-gradient-to-br from-amber-500 via-orange-500 to-amber-600',
    iconInnerBg: 'bg-gradient-to-br from-amber-600 to-orange-600',
    iconRing: 'ring-amber-300/60',
    iconShadow: '0 8px 32px rgba(217, 119, 6, 0.4)',
    hex: '#D97706',
    hexLight: '#FFFBEB',
    hexDark: '#92400E'
  },
  mining: {
    id: 'mining',
    name: 'Mining & Resources',
    shortName: 'Mining',
    icon: Mountain,
    gradient: 'from-stone-600 via-stone-700 to-stone-800',
    gradientHover: 'from-stone-700 via-stone-800 to-stone-900',
    gradientLight: 'from-stone-100 via-stone-100 to-stone-100',
    gradientSubtle: 'from-stone-50 via-stone-50 to-stone-50',
    primary: '#78716C',
    primaryLight: '#A8A29E',
    primaryDark: '#57534E',
    secondary: '#D97706',
    accent: '#B45309',
    cardBg: 'bg-stone-900/60',
    cardBorder: 'border-stone-700/50',
    textPrimary: 'text-stone-100',
    textSecondary: 'text-stone-400',
    badgeBg: 'bg-stone-700/30',
    badgeText: 'text-stone-300',
    buttonBg: 'bg-stone-600',
    buttonText: 'text-stone-100',
    iconBg: 'bg-stone-800/40',
    iconText: 'text-stone-300',
    iconRing: 'ring-stone-300/60',
    iconShadow: '0 8px 32px rgba(120, 113, 108, 0.4)',
    hex: '#78716C',
    hexLight: '#F5F5F4',
    hexDark: '#44403C'
  },
  utilities: {
    id: 'utilities',
    name: 'Utilities & Energy',
    shortName: 'Utilities',
    icon: Zap,
    gradient: 'from-yellow-500 via-yellow-600 to-orange-600',
    gradientHover: 'from-yellow-600 via-yellow-700 to-orange-700',
    gradientLight: 'from-yellow-100 via-yellow-100 to-orange-100',
    gradientSubtle: 'from-yellow-50 via-yellow-50 to-orange-50',
    primary: '#EAB308',
    primaryLight: '#FACC15',
    primaryDark: '#CA8A04',
    secondary: '#F97316',
    accent: '#EA580C',
    cardBg: 'bg-yellow-900/60',
    cardBorder: 'border-yellow-700/50',
    textPrimary: 'text-yellow-100',
    textSecondary: 'text-yellow-400',
    badgeBg: 'bg-yellow-700/30',
    badgeText: 'text-yellow-300',
    buttonBg: 'bg-yellow-600',
    buttonText: 'text-yellow-100',
    iconBg: 'bg-yellow-800/40',
    iconText: 'text-yellow-300',
    iconRing: 'ring-yellow-300/60',
    iconShadow: '0 8px 32px rgba(234, 179, 8, 0.4)',
    hex: '#EAB308',
    hexLight: '#FEFCE8',
    hexDark: '#854D0E'
  },
  admin_services: {
    id: 'admin_services',
    name: 'Admin & Support Services',
    shortName: 'Admin Services',
    icon: ClipboardList,
    gradient: 'from-gray-500 via-gray-600 to-gray-700',
    gradientHover: 'from-gray-600 via-gray-700 to-gray-800',
    gradientLight: 'from-gray-100 via-gray-100 to-gray-100',
    gradientSubtle: 'from-gray-50 via-gray-50 to-gray-50',
    primary: '#6B7280',
    primaryLight: '#9CA3AF',
    primaryDark: '#4B5563',
    secondary: '#374151',
    accent: '#1F2937',
    cardBg: 'bg-gray-900/60',
    cardBorder: 'border-gray-700/50',
    textPrimary: 'text-gray-100',
    textSecondary: 'text-gray-400',
    badgeBg: 'bg-gray-700/30',
    badgeText: 'text-gray-300',
    buttonBg: 'bg-gray-600',
    buttonText: 'text-gray-100',
    iconBg: 'bg-gray-800/40',
    iconText: 'text-gray-300',
    iconRing: 'ring-gray-300/60',
    iconShadow: '0 8px 32px rgba(107, 114, 128, 0.4)',
    hex: '#6B7280',
    hexLight: '#F9FAFB',
    hexDark: '#374151'
  },
  public_admin: {
    id: 'public_admin',
    name: 'Public Administration & Safety',
    shortName: 'Public Admin',
    icon: Shield,
    gradient: 'from-indigo-700 via-indigo-800 to-blue-900',
    gradientHover: 'from-indigo-800 via-indigo-900 to-blue-950',
    gradientLight: 'from-indigo-100 via-indigo-100 to-blue-100',
    gradientSubtle: 'from-indigo-50 via-indigo-50 to-blue-50',
    primary: '#4338CA',
    primaryLight: '#6366F1',
    primaryDark: '#3730A3',
    secondary: '#1E3A5F',
    accent: '#1E40AF',
    cardBg: 'bg-indigo-900/60',
    cardBorder: 'border-indigo-700/50',
    textPrimary: 'text-indigo-100',
    textSecondary: 'text-indigo-400',
    badgeBg: 'bg-indigo-700/30',
    badgeText: 'text-indigo-300',
    buttonBg: 'bg-indigo-600',
    buttonText: 'text-indigo-100',
    iconBg: 'bg-indigo-800/40',
    iconText: 'text-indigo-300',
    iconRing: 'ring-indigo-300/60',
    iconShadow: '0 8px 32px rgba(67, 56, 202, 0.4)',
    hex: '#4338CA',
    hexLight: '#EEF2FF',
    hexDark: '#312E81'
  }
};

// Helper function to get industry UI config
export const getIndustryUIConfig = (industryId) => {
  if (!industryId) return INDUSTRY_UI_CONFIG.professional;
  const normalizedId = industryId.toLowerCase().trim();
  return INDUSTRY_UI_CONFIG[normalizedId] || INDUSTRY_UI_CONFIG.professional;
};

// List of industries for iteration
export const INDUSTRY_UI_LIST = Object.values(INDUSTRY_UI_CONFIG);

// Quick reference color guide for easy identification:
// Hospitality   -> Deep Purple (#9333EA)
// Retail        -> Blue (#3B82F6)
// Healthcare    -> Hot Pink (#EC4899)
// Professional  -> Navy Slate (#475569)
// Fitness       -> Lime Green (#84CC16)
// Property      -> Orange (#F97316)
// Construction  -> Yellow (#EAB308)
// Automotive    -> Red (#EF4444)
// Education     -> Teal (#14B8A6)
// Beauty        -> Fuchsia (#D946EF)
// Manufacturing -> Steel Gray (#71717A)
// Tourism       -> Sky Blue (#0EA5E9)
// CRM           -> Violet (#8B5CF6)
// Technology    -> Cyan (#06B6D4)
// Finance       -> Emerald (#10B981)
// QSR           -> Amber (#D97706)
// Mining        -> Stone (#78716C)
// Utilities     -> Yellow-Orange (#EAB308)
// Admin Svcs    -> Gray (#6B7280)
// Public Admin  -> Indigo (#4338CA)

export default INDUSTRY_UI_CONFIG;
